import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Land-as-Equity Consulting", item: "https://cashmyproperty.ai/services/land-as-equity" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What does 'Land-as-Equity' actually mean?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Land-as-Equity means your land becomes your capital contribution to a business venture. Instead of selling your land for cash, you contribute it as equity to a partnership — for example, an eco-resort developer builds and operates the resort on your land, and you receive a share of the profits. You retain ownership while earning from the business.",
      },
    },
    {
      "@type": "Question",
      name: "What types of businesses work with Land-as-Equity?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The most successful Land-as-Equity models include glamping resorts and eco-tourism, solar farms (25-year power purchase agreements), organic farming and agritourism, wellness retreats, adventure sports parks, and film/tourism location rentals. Scenic, remote, or large agricultural land is ideal for these ventures.",
      },
    },
    {
      "@type": "Question",
      name: "How is profit shared in a Land-as-Equity partnership?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Profit sharing is negotiated case by case. For eco-resorts, landowners typically receive 20–35% of operating profits plus a base rental. For solar farms, you receive a fixed lease payment with annual escalation for 25 years. For organic farming, profit sharing ranges from 30–50% depending on your level of involvement.",
      },
    },
  ],
};

const steps = [
  { step: "01", title: "Land Character Assessment", desc: "Our AI evaluates your land's natural features — terrain, water bodies, forest cover, climate, accessibility, and tourism catchment. We identify which business models have the highest probability of success.", icon: "ri-landscape-line" },
  { step: "02", title: "Business Model Design", desc: "Based on the assessment, we design 2–3 viable business models — glamping resort, solar farm, organic agriculture, adventure park, wellness retreat — with projected revenue, costs, and your equity share.", icon: "ri-lightbulb-flash-line" },
  { step: "03", title: "Operator / Investor Matching", desc: "We connect you with hospitality operators, renewable energy developers, agritech companies, and impact investors actively seeking land for their projects. You choose the best partner.", icon: "ri-user-search-line" },
  { step: "04", title: "Partnership Structuring", desc: "Legal structuring of the equity partnership — defining your ownership percentage, profit sharing, operational control, exit clauses, and dispute resolution. Fully transparent and owner-protective.", icon: "ri-scales-3-line" },
  { step: "05", title: "Launch & Revenue Share", desc: "The partner builds and operates the business. You receive profit share + base rent (where applicable). Our team provides ongoing monitoring to ensure the partnership terms are honored.", icon: "ri-funds-line" },
];

const models = [
  { icon: "ri-hotel-line", title: "Glamping & Eco-Resorts", desc: "Luxury tented resorts, treehouses, and eco-cottages on scenic land. Operators bring design, construction, and hospitality expertise. You earn 20–35% profit share + base rent.", landType: "Scenic hills, waterfront, forest edge", incomeType: "Profit share + base rent" },
  { icon: "ri-sun-line", title: "Solar Farms", desc: "Large-scale solar installations under 25-year government power purchase agreements. Developer handles installation, grid connection, and maintenance. You earn fixed lease with annual escalation.", landType: "Flat, arid, 5+ acres, good sun exposure", incomeType: "Fixed lease, 25-year PPA" },
  { icon: "ri-plant-line", title: "Organic Farming & Agritourism", desc: "Certified organic farms with farm-stay experiences, farm-to-table restaurants, and weekend markets. Attracts urban visitors seeking authentic rural experiences.", landType: "Agricultural land with water access", incomeType: "30–50% profit share" },
  { icon: "ri-compass-3-line", title: "Adventure & Activity Parks", desc: "Zip-lining, rock climbing, ATV trails, camping grounds. Low infrastructure, high experience value. Perfect for hilly terrain near tourist corridors.", landType: "Hilly terrain, near tourist routes", incomeType: "25–35% profit share" },
  { icon: "ri-mental-health-line", title: "Wellness & Yoga Retreats", desc: "Wellness resorts, Ayurveda centers, and yoga retreats on serene land. Growing demand from domestic and international wellness tourists.", landType: "Peaceful, natural surroundings", incomeType: "20–30% profit share + base rent" },
  { icon: "ri-camera-lens-line", title: "Film & Event Locations", desc: "Unique landscapes rented as film shooting locations, wedding venues, and corporate offsite destinations. High per-day revenue with zero construction.", landType: "Unique terrain, good accessibility", incomeType: "Per-day location fee" },
];

const benefits = [
  { icon: "ri-hand-heart-line", title: "Retain Full Ownership", desc: "Your name stays on the title deed. You are an equity partner — not a seller. The land remains your family's generational asset while generating income." },
  { icon: "ri-funds-box-line", title: "No Investment Needed", desc: "The operator or investor funds 100% of business setup, construction, and operations. Your contribution is the land — no cash required from you." },
  { icon: "ri-global-line", title: "Government Incentives", desc: "Solar farms benefit from 25-year PPAs and accelerated depreciation. Eco-tourism and organic farming qualify for central and state government subsidies — we help you claim them." },
  { icon: "ri-bar-chart-grouped-line", title: "Dual Income Streams", desc: "Most Land-as-Equity partnerships include both a base rental component and profit sharing — giving you stable minimum income plus upside from business performance." },
  { icon: "ri-recycle-line", title: "Sustainable Development", desc: "These models preserve your land's natural character. No concrete jungles, no environmental damage. Sustainable businesses that enhance — not destroy — your land." },
  { icon: "ri-rocket-line", title: "Scalable Model", desc: "Once the first business succeeds, your land's profile rises. Additional partnerships, event bookings, and brand collaborations follow — multiplying your income streams." },
];

const faqs = [
  { q: "What does 'Land-as-Equity' actually mean?", a: "Land-as-Equity means your land becomes your capital contribution to a business venture. Instead of selling your land for cash, you contribute it as equity to a partnership — for example, an eco-resort developer builds and operates the resort on your land, and you receive a share of the profits. You retain ownership while earning from the business." },
  { q: "What types of businesses work with Land-as-Equity?", a: "The most successful Land-as-Equity models include glamping resorts and eco-tourism, solar farms (25-year power purchase agreements), organic farming and agritourism, wellness retreats, adventure sports parks, and film/tourism location rentals. Scenic, remote, or large agricultural land is ideal for these ventures." },
  { q: "How is profit shared in a Land-as-Equity partnership?", a: "Profit sharing is negotiated case by case. For eco-resorts, landowners typically receive 20–35% of operating profits plus a base rental. For solar farms, you receive a fixed lease payment with annual escalation for 25 years. For organic farming, profit sharing ranges from 30–50% depending on your level of involvement." },
];

export default function LandAsEquityServicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Land-as-Equity Consulting — Turn Scenic Land Into a Business | CashMyProperty.ai",
    description: "Convert your scenic, remote, or agricultural land into equity in profitable businesses — glamping resorts, solar farms, organic farming, wellness retreats. Retain full ownership, earn profit share + base rent, zero investment. Government subsidies available.",
    canonicalUrl: "https://cashmyproperty.ai/services/land-as-equity",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Luxury%20glamping%20eco%20resort%20with%20transparent%20dome%20tents%20and%20treehouses%20at%20twilight%2C%20warm%20glowing%20interiors%20visible%2C%20forest%20and%20mountain%20surroundings%2C%20dramatic%20starry%20sky%2C%20soft%20amber%20path%20lights%2C%20cinematic%20travel%20photography%2C%20deep%20teal%20and%20gold%20color%20palette%2C%20India%20nature%20retreat%2C%20ultra%20detailed%20atmospheric&width=1800&height=800&seq=service-detail-equity-hero&orientation=landscape"
          alt="Land-as-Equity Consulting — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Land-as-Equity</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Consulting</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Your scenic land becomes your equity. Earn from eco-resorts, solar farms, and organic ventures — without selling a square foot.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Land-as-Equity Consulting</span>
          </nav>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What is Land-as-Equity?</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Your Land = Your Capital
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Not every plot of land is suitable for apartments or warehouses — but that does not mean it cannot generate wealth. Scenic, remote, agricultural, or ecologically unique land has its own monetization path: <strong className="text-foreground-950">Land-as-Equity</strong>.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Instead of selling your land for cash or waiting for a developer, you contribute it as equity to a <strong className="text-foreground-950">revenue-generating business</strong>. A hospitality operator builds a glamping resort. A renewable energy company installs a solar farm. An agritech startup develops an organic farming operation. You retain ownership and earn a share of the profits.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                This is the ideal strategy for landowners whose property is <strong className="text-foreground-950">too beautiful, too remote, or too large</strong> for conventional development — but perfect for experience-driven or sustainable businesses.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20eco%20resort%20with%20infinity%20pool%20overlooking%20mountain%20valley%20at%20sunset%2C%20modern%20sustainable%20architecture%2C%20warm%20ambient%20lighting%2C%20dramatic%20golden%20hour%20sky%2C%20cinematic%20travel%20photography%2C%20deep%20teal%20and%20amber%20color%20palette%2C%20India%20hill%20station%20retreat%2C%20ultra%20detailed&width=800&height=600&seq=equity-eco-resort-01&orientation=landscape"
                alt="Land-as-Equity"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Business Models */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Business Models</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">6 Ways to Monetize Unique Land</h2>
            <p className="text-foreground-600">Proven business models that turn scenic and remote land into income-generating assets.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {models.map((m) => (
              <div key={m.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300 h-full flex flex-col">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${m.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{m.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">{m.desc}</p>
                <div className="space-y-2 pt-4 border-t border-primary-900/20">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-map-pin-line text-primary-500" />
                    {m.landType}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-money-rupee-circle-line text-primary-500" />
                    {m.incomeType}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">From Raw Land to Operating Business</h2>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Land-as-Equity is the Smartest Choice for Unique Land</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "20–50%", label: "Profit Share Range" },
              { value: "25 Years", label: "Solar Farm PPA Duration" },
              { value: "₹0", label: "Your Cash Investment" },
              { value: "100%", label: "Ownership Retained" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Got Scenic or Remote Land? Let's Find Your Business Model.
          </h2>
          <p className="text-foreground-600 mb-8">
            Our AI will analyze your land's natural features and match it with the most profitable Land-as-Equity opportunity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Equity Assessment — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}